import { createContext, useContext, useMemo, useState } from "react";

import classnames from "classnames";
import moment from "moment";

const TimeScopeContext = createContext({
  frequency: "",
  setFrequency: (_frequency: string) => {},
  timeScope: "",
  setTimeScope: (_timeScope: string) => {},
  isAnnual: false,
  showSemiAnnualTimeScope: false,
  setShowSemiAnnualTimeScope: (_show: boolean) => {},
  showQuarterTimeScope: false,
  setShowQuarterTimeScope: (_show: boolean) => {},
});

const availableFrequencyOptions = [
  {
    id: "annually",
    text: "Annual",
  },
  {
    id: "semi-annually",
    text: "Semi Annual",
  },
  {
    id: "quarterly",
    text: "Quarterly",
  },
];

const timeScopeMapping = {
  quarterly: [
    {
      id: "5Q",
      text: "5Q",
    },
    {
      id: "10Q",
      text: "10Q",
    },
    {
      id: "20Q",
      text: "20Q",
    },
  ],
  "semi-annually": [
    {
      id: "5H",
      text: "5H",
    },
    {
      id: "7H",
      text: "7H",
    },
    {
      id: "10H",
      text: "10H",
    },
  ],
  annually: [
    {
      id: "5Y",
      text: "5Y",
    },
    {
      id: "7Y",
      text: "7Y",
    },
    {
      id: "10Y",
      text: "10Y",
    },
  ],
};

export const TimeScopeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [frequency, setFrequency] = useState("annually");
  const [quarterTimeScope, setQuarterTimeScope] = useState("20Q");
  const [semiAnnualTimeScope, setSemiAnnualTimeScope] = useState("10H");
  const [annualTimeScope, setAnnualTimeScope] = useState("10Y");
  const [showSemiAnnualTimeScope, setShowSemiAnnualTimeScope] = useState(false);
  const [showQuarterTimeScope, setShowQuarterTimeScope] = useState(false);

  const isAnnual = frequency === "annually";

  const timeScope =
    frequency === "annually"
      ? annualTimeScope
      : frequency === "semi-annually"
      ? semiAnnualTimeScope
      : quarterTimeScope;
  const setTimeScope =
    frequency === "annually"
      ? setAnnualTimeScope
      : frequency === "semi-annually"
      ? setSemiAnnualTimeScope
      : setQuarterTimeScope;

  return (
    <TimeScopeContext.Provider
      value={{
        frequency,
        setFrequency,
        timeScope,
        setTimeScope,
        isAnnual,
        showSemiAnnualTimeScope,
        setShowSemiAnnualTimeScope,
        showQuarterTimeScope,
        setShowQuarterTimeScope,
      }}
    >
      {children}
    </TimeScopeContext.Provider>
  );
};

export const useTimeScope = () => {
  const {
    isAnnual,
    frequency,
    timeScope,
    showQuarterTimeScope,
    setShowQuarterTimeScope,
    showSemiAnnualTimeScope,
    setShowSemiAnnualTimeScope,
  } = useContext(TimeScopeContext);

  const startDate = useMemo(() => {
    const matches = timeScope.match(/(\d+)([Y|Q|H])/);
    if (!matches) {
      return;
    }
    let value = parseInt(matches[1], 10);
    if (frequency === "semi-annually") {
      value = value / 2;
    }
    const unit = frequency !== "quarterly" ? "year" : "quarter";
    return moment().startOf(unit).subtract(value, unit);
  }, [frequency, timeScope]);

  return {
    isAnnual,
    frequency,
    startDate,
    showQuarterTimeScope,
    setShowQuarterTimeScope,
    showSemiAnnualTimeScope,
    setShowSemiAnnualTimeScope,
  };
};

const ToggleTimeScope = () => {
  const {
    frequency,
    setFrequency,
    timeScope,
    setTimeScope,
    showQuarterTimeScope,
    showSemiAnnualTimeScope,
  } = useContext(TimeScopeContext);

  const timeScopeOptions =
    timeScopeMapping[frequency as keyof typeof timeScopeMapping];

  let frequencyOptions = availableFrequencyOptions;
  if (!showQuarterTimeScope) {
    frequencyOptions = frequencyOptions.filter((o) => o.id !== "quarterly");
  }
  if (!showSemiAnnualTimeScope) {
    frequencyOptions = frequencyOptions.filter((o) => o.id !== "semi-annually");
  }

  return (
    <div className="flex justify-between">
      <ToggleButtons
        options={frequencyOptions}
        value={frequency}
        setValue={setFrequency}
      />
      <ToggleButtons
        options={timeScopeOptions}
        value={timeScope}
        setValue={setTimeScope}
      />
    </div>
  );
};

export const ToggleButtons = ({
  options,
  setValue,
  value,
  buttonClassName,
}: {
  options: { id: string; text: string }[];
  setValue: (_value: string) => void;
  value: string;
  buttonClassName?: (active: boolean) => string;
}) => {
  return (
    <div>
      {options.map((option) => (
        <button
          className={classnames(
            "px-3 py-2 border border-gray-300",
            value === option.id
              ? "bg-[#24a9a7] border-transparent text-white"
              : "bg-transparent border-solid border-[#24a9a7] text-active-black",
            buttonClassName?.(value === option.id)
          )}
          key={option.id}
          onClick={() => setValue(option.id)}
        >
          {option.text}
        </button>
      ))}
    </div>
  );
};

export default ToggleTimeScope;
